import {postProcessingSettingsToImageProcessingSettings} from "@app/template-editor/helpers/template-jobs"
import type {CmLogger} from "@cm/log"
import {ImageProcessingNodes} from "@cm/image-processing-nodes"
import {TileableMaterialPostRenderStepInput, tileableMaterialPostRenderStepTask} from "@cm/job-nodes/asset-rendering"
import {imageProcessingTask} from "@cm/job-nodes/image-processing"
import {CreateJobGraphData, JobNodes} from "@cm/job-nodes/job-nodes"
import {cmRenderTaskForPassNames} from "@cm/job-nodes/rendering"
import {createPostProcessingGraph, Utility} from "@cm/job-nodes/utility"
import {DataResolver, MaterialGraph} from "@cm/material-nodes"
import {SceneNodes} from "@cm/template-nodes"
import {CM_TO_INCH, tileParamsForMaterial} from "@common/helpers/rendering/material/material-assets-rendering/common"
import {JobGraphMaterial} from "@common/models/material-assets-rendering/job-graph-material"
import {Settings} from "@common/models/settings/settings"
import {generateThumbnailsDataObjectTask} from "@cm/job-nodes"

export const TILE_PADDING_FRACTION = 0.1
export const TILE_MIN_PADDING = 200

export function jobGraphFn_tile(
    renderGraphDataObjectId: number,
    materialDetails: JobGraphMaterial,
    widthCm: number,
    heightCm: number,
    width: number,
    height: number,
    paddingWidth: number,
    paddingHeight: number,
    filenames: TileableMaterialPostRenderStepInput["filenames"],
    postProcessingSettings: SceneNodes.RenderPostProcessingSettings | undefined,
): CreateJobGraphData {
    const mainRenderTask = JobNodes.task(cmRenderTaskForPassNames("Combined"), {
        input: JobNodes.value({
            renderGraph: JobNodes.dataObjectReference(renderGraphDataObjectId),
            customerId: materialDetails.organization.legacyId,
            previewForProgressValue: [],
        }),
    })

    const combinedRenderTask = JobNodes.struct({
        renderPasses: JobNodes.get(mainRenderTask, "renderPasses"),
        preview: JobNodes.get(mainRenderTask, "preview"),
        metadata: JobNodes.get(mainRenderTask, "metadata"),
    })

    const createPostProcessingGraphTask = JobNodes.task(createPostProcessingGraph, {
        input: JobNodes.struct({
            data: combinedRenderTask,
            settings: JobNodes.value(postProcessingSettings ? postProcessingSettingsToImageProcessingSettings(postProcessingSettings) : undefined),
            cryptomatteManifest: JobNodes.struct({
                type: JobNodes.value("externalData" as const),
                resolveTo: JobNodes.value("cryptomatteManifest" as const),
                sourceData: JobNodes.get(combinedRenderTask, "metadata"),
            }),
        }),
    })

    const croppedImage: JobNodes.TypedDataNode<ImageProcessingNodes.CropImage> = JobNodes.struct({
        type: JobNodes.value("crop" as const),
        input: JobNodes.get(createPostProcessingGraphTask, "image"),
        region: JobNodes.value({
            type: "region" as const,
            region: [paddingWidth, paddingHeight, width, height] as ImageProcessingNodes.BBox,
        }),
    })
    const dpiX = width / (widthCm * CM_TO_INCH)
    const dpiY = height / (heightCm * CM_TO_INCH)
    const dpi = (dpiX + dpiY) / 2
    const setDpi: JobNodes.TypedDataNode<ImageProcessingNodes.SetDpi> = JobNodes.struct({
        type: JobNodes.value("setDpi" as const),
        input: croppedImage,
        dpi: JobNodes.value(dpi),
    })
    const encodedImage: JobNodes.TypedDataNode<ImageProcessingNodes.Encode> = JobNodes.struct({
        type: JobNodes.value("encode" as const),
        input: setDpi,
        mediaType: JobNodes.value("image/x-exr"),
    })
    const imgProcTask = JobNodes.task(imageProcessingTask, {input: JobNodes.struct({graph: encodedImage})})

    const uploadProcessingTaskNode = JobNodes.task(generateThumbnailsDataObjectTask, {
        input: Utility.DataObject.update(imgProcTask, {imageDataType: "COLOR" as const}),
    })

    const postRenderTask = JobNodes.task(tileableMaterialPostRenderStepTask, {
        input: JobNodes.struct({
            renderImage: JobNodes.get(uploadProcessingTaskNode, "dataObject"),
            materialId: JobNodes.value(materialDetails.legacyId),
            customerId: JobNodes.value(materialDetails.organization.legacyId),
            filenames: JobNodes.value(filenames),
        }),
    })

    return JobNodes.jobGraph(postRenderTask, {
        progress: {
            type: "progressGroup",
            items: [
                {node: mainRenderTask, factor: 15},
                {node: uploadProcessingTaskNode, factor: 2},
                {node: postRenderTask, factor: 4},
            ],
        },
        platformVersion: Settings.APP_VERSION,
    })
}

/**reduces padded image size to maxNumPixels by adjusting the size of the unpadded image and keeping its aspect ratio, assumptions:
 * scaleFactorWidth scales the original width to the padded width
 * scaleFactorWidth * repeatWidth * scaleFactorHeight * repeatHeight = maxNumPixels
 * repeatWidth/repeatHeight = ratio
 */
function resizeTileIfNeeded(repeatWidth: number, repeatHeight: number, repeatWidthCm: number, pxPerCm: number) {
    const maxNumPixels = 16000 * 16000 // when x * y * 4 Channels * 4 Bytes > 2^32 Bytes, rendering fails because only 2^32 Bytes can be addressed.
    const scaleFactorHeight = 1 + TILE_PADDING_FRACTION + TILE_PADDING_FRACTION
    const scaleFactorWidth = 1 + TILE_PADDING_FRACTION + TILE_PADDING_FRACTION
    if (scaleFactorWidth * repeatWidth * scaleFactorHeight * repeatHeight > maxNumPixels) {
        const ratio = repeatWidth / repeatHeight
        repeatHeight = Math.round(Math.sqrt(maxNumPixels / (ratio * scaleFactorHeight * scaleFactorWidth)))
        repeatWidth = Math.round(repeatHeight * ratio)
        pxPerCm = repeatWidth / repeatWidthCm
    }

    return {repeatWidth, repeatHeight, pxPerCm}
}

export async function tileGenerationParamsForMaterial(materialGraph: MaterialGraph, resolve: DataResolver, logger: CmLogger) {
    const {
        repeatWidthCm,
        repeatHeightCm,
        repeatWidth: initialRepeatWidth,
        repeatHeight: initialRepeatHeight,
        pxPerCm: initialPxPerCm,
    } = await tileParamsForMaterial(materialGraph, resolve, logger)
    const {repeatWidth, repeatHeight, pxPerCm} = resizeTileIfNeeded(initialRepeatWidth, initialRepeatHeight, repeatWidthCm, initialPxPerCm)

    const paddingWidth = Math.max(Math.round(TILE_PADDING_FRACTION * repeatWidth), TILE_MIN_PADDING)
    const paddingHeight = Math.max(Math.round(TILE_PADDING_FRACTION * repeatHeight), TILE_MIN_PADDING)
    const paddingWidthCm = paddingWidth / pxPerCm
    const paddingHeightCm = paddingHeight / pxPerCm

    const paddedWidth = repeatWidth + 2 * paddingWidth
    const paddedHeight = repeatHeight + 2 * paddingHeight
    const paddedWidthCm = repeatWidthCm + 2 * paddingWidthCm
    const paddedHeightCm = repeatHeightCm + 2 * paddingHeightCm

    const offsetX = repeatWidthCm / 2
    const offsetY = repeatHeightCm / 2

    return {
        repeatWidth,
        repeatHeight,
        repeatWidthCm,
        repeatHeightCm,
        paddingWidth,
        paddingHeight,
        paddedWidth,
        paddedHeight,
        paddedWidthCm,
        paddedHeightCm,
        offsetX,
        offsetY,
        pxPerCm,
    }
}
